// District world: two 80-unit tiles that scroll and wrap, carrying buildings,
// vegetation, props AND the textured ground strips — the ground must scroll
// with the world or its texture grain visibly stays glued under the player
// (v1 scrolled its road segments the same way, js/world-manager.js:2735-2741).
// A static flat-color horizon plane covers the far, fully-fogged zone.

import * as THREE from 'three';
import { GROUND_COLORS, type DistrictDef } from '@/content/districts';
import type { Rng } from '@/core/rng';
import { buildBuildingsIntoTile } from '@/world/buildings';
import { buildVegetationIntoTile } from '@/world/vegetation';
import { buildPropsIntoTile } from '@/world/props';
import { repeated, texturedLambert, type WorldTextures } from '@/render/textures';

const TILE_LENGTH = 80;
// Wrap once a tile's ground is fully behind the player (player z=2): with
// two tiles this guarantees continuous ground from z≈8 back to z≈-72
const WRAP_Z = TILE_LENGTH + 8;

export class World {
  private readonly root = new THREE.Group();
  private readonly staticGround = new THREE.Group();
  private tiles: THREE.Group[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    district: DistrictDef,
    rng: Rng,
    private readonly textures: WorldTextures,
  ) {
    scene.add(this.root);
    this.root.add(this.staticGround);
    this.buildGround(district);
    this.buildTiles(district, rng);
  }

  /** Swap the world to a new district (new run). */
  rebuild(district: DistrictDef, rng: Rng): void {
    for (const tile of this.tiles) {
      this.root.remove(tile);
      disposeGroup(tile);
    }
    this.tiles = [];
    this.staticGround.clear();
    this.buildGround(district);
    this.buildTiles(district, rng);
  }

  /** Far horizon plane only: flat colors, sunk slightly below the tile
   *  ground, always deep in fog — covers the brief coverage gap ahead of
   *  the two scrolling tiles. */
  private buildGround(district: DistrictDef): void {
    const green = district.greenery >= 0.4;
    const base = new THREE.Mesh(
      new THREE.BoxGeometry(80, 0.04, 200),
      new THREE.MeshLambertMaterial({ color: green ? 0x6faa5a : 0x8f948a }),
    );
    base.position.set(0, -0.045, -155);
    this.staticGround.add(base);

    const street = new THREE.Mesh(
      new THREE.BoxGeometry(6.5, 0.1, 200),
      new THREE.MeshLambertMaterial({ color: GROUND_COLORS.street }),
    );
    street.position.set(0, -0.025, -155);
    this.staticGround.add(street);

    for (const side of [-1, 1]) {
      const pavement = new THREE.Mesh(
        new THREE.BoxGeometry(3.5, 0.16, 200),
        new THREE.MeshLambertMaterial({ color: 0xbbbbbb }),
      );
      pavement.position.set(side * 4.6, -0.025, -155);
      this.staticGround.add(pavement);
    }
  }

  /** Textured ground strips inside a scrolling tile. Repeat counts are whole
   *  numbers per 80 units so adjacent tiles join seamlessly. */
  private buildGroundIntoTile(tile: THREE.Group, district: DistrictDef): void {
    const zCenter = -TILE_LENGTH / 2;
    const green = district.greenery >= 0.4;

    const base = new THREE.Mesh(
      new THREE.BoxGeometry(80, 0.04, TILE_LENGTH),
      texturedLambert(
        green ? 0x6faa5a : 0x8f948a,
        repeated(green ? this.textures.grass : null, 20, 20),
      ),
    );
    base.position.set(0, -0.02, zCenter);
    base.receiveShadow = true;
    tile.add(base);

    // Street bed and pavements; heights match player ground levels
    // (street 0.05, pavement 0.08), colors from v1 COLORS.GROUND
    const street = new THREE.Mesh(
      new THREE.BoxGeometry(6.5, 0.1, TILE_LENGTH),
      texturedLambert(0x8a8a8a, repeated(this.textures.asphalt, 2, 24)),
    );
    if (!this.textures.asphalt) {
      street.material.color.setHex(GROUND_COLORS.street);
    }
    street.position.set(0, 0, zCenter);
    street.receiveShadow = true;
    tile.add(street);

    for (const side of [-1, 1]) {
      const pavement = new THREE.Mesh(
        new THREE.BoxGeometry(3.5, 0.16, TILE_LENGTH),
        texturedLambert(0xbbbbbb, repeated(this.textures.pavement, 3, 32)),
      );
      pavement.position.set(side * 4.6, 0, zCenter);
      pavement.receiveShadow = true;
      tile.add(pavement);

      // Curb between street and pavement
      const curb = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.14, TILE_LENGTH),
        new THREE.MeshLambertMaterial({ color: GROUND_COLORS.concrete }),
      );
      curb.position.set(side * 2.9, 0.02, zCenter);
      tile.add(curb);
    }
  }

  private buildTiles(district: DistrictDef, rng: Rng): void {
    for (let t = 0; t < 2; t++) {
      const tile = new THREE.Group();
      this.buildGroundIntoTile(tile, district);
      this.buildCenterLine(tile);
      buildBuildingsIntoTile(tile, district, rng, this.textures);
      buildVegetationIntoTile(tile, district, rng);
      buildPropsIntoTile(tile, rng);
      tile.position.z = -t * TILE_LENGTH;
      this.root.add(tile);
      this.tiles.push(tile);
    }
  }

  private buildCenterLine(tile: THREE.Group): void {
    const mat = new THREE.MeshBasicMaterial({ color: 0xdddddd });
    const geo = new THREE.BoxGeometry(0.12, 0.02, 2);
    for (let z = -TILE_LENGTH; z < 0; z += 6) {
      const dash = new THREE.Mesh(geo, mat);
      dash.position.set(0, 0.06, z);
      tile.add(dash);
    }
  }

  /** World scroll (v1 formula: z += gameSpeed * dt * 60). */
  update(dt: number, gameSpeed: number): void {
    const dz = gameSpeed * dt * 60;
    for (const tile of this.tiles) {
      tile.position.z += dz;
      if (tile.position.z > WRAP_Z) tile.position.z -= TILE_LENGTH * 2;
    }
  }
}

function disposeGroup(group: THREE.Group): void {
  group.traverse((obj) => {
    if (obj instanceof THREE.InstancedMesh) {
      obj.dispose(); // Frees instance buffers; geometry is shared, keep it
    } else if (obj instanceof THREE.Mesh) {
      const geometry = (obj as THREE.Mesh).geometry;
      geometry.dispose(); // Building/roof/dash geometries are per-tile
    }
    // Materials are intentionally not disposed: they're either module-shared
    // or tiny untextured Lambert/Toon instances; the toon cache is cleared on
    // rebuild so nothing accumulates across runs.
  });
}
